/** The three start-screen texts a white-label brand may write itself. */
export type BrandCopyKey = "kicker" | "headline" | "intro";

/**
 * The `copy` block of a brand manifest, keyed by store locale (`nl-NL`, `en-US`).
 * Everything is optional: a brand without the block keeps the translated defaults.
 */
export type BrandCopy = Record<string, Partial<Record<BrandCopyKey, string>>>;

const valueOr = (value: string | undefined, fallback: string): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

/**
 * The `copy` block of the brand manifest, compiled in as JSON by
 * `scripts/whitelabel.mjs`. This runs at module load, so a value that is missing
 * or damaged has to become "no brand copy" instead of an exception.
 */
export const parseBrandCopy = (value: string | undefined): BrandCopy => {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as BrandCopy) : {};
  } catch {
    return {};
  }
};

/**
 * The heading font a white-label brand may set for itself: either woff2 files it
 * ships (staged at `/brand/fonts/`) or one foundry stylesheet, never both.
 */
export interface BrandHeadingFont {
  family?: string;
  weight?: number;
  files?: string[];
  stylesheet?: string;
}

/** Same rule as `parseBrandCopy`: this runs at module load, so damage becomes "no font". */
export const parseBrandFont = (value: string | undefined): BrandHeadingFont => {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as BrandHeadingFont)
      : {};
  } catch {
    return {};
  }
};

/**
 * Everything an `<img>` needs for one logo variant, on top of the file itself.
 *
 * Coachio ships checked-in bitmaps in `public/` at three widths, so its wordmarks
 * carry a 2x source and their intrinsic size. A white-label brand ships one SVG,
 * which scales on its own and has neither. Keyed by the file the manifest names,
 * so a brand that is handed a different file gets the plain `src` and nothing else
 * rather than Coachio's numbers.
 *
 * The square `logo-dark.png` (1000x1000) is the app icon, not a wordmark: pointing
 * a signed-out screen at it draws a 150x150 box with a tiny logo in the middle.
 */
const SIZED_LOGOS: Record<string, { srcset: string; width: number; height: number }> = {
  "/logo-light-150.png": {
    srcset: "/logo-light-150.png 1x, /logo-light-300.png 2x",
    width: 150,
    height: 30,
  },
  "/logo-dark-150.png": {
    srcset: "/logo-dark-150.png 1x, /logo-dark-300.png 2x",
    width: 150,
    height: 29,
  },
};

const logoLight = valueOr(import.meta.env.VITE_BRAND_LOGO_LIGHT, "/logo-light-150.png");
const logoDark = valueOr(import.meta.env.VITE_BRAND_LOGO_DARK, "/logo-dark-150.png");

/**
 * Public identity compiled into one distributable app.
 *
 * None of these values is a credential. The API maps `id` to a tenant and is
 * responsible for checking membership before issuing or accepting a token.
 */
export const DISTRIBUTION = Object.freeze({
  id: valueOr(import.meta.env.VITE_DISTRIBUTION_ID, "coachio"),
  displayName: valueOr(import.meta.env.VITE_BRAND_NAME, "Coachio"),
  primaryColor: valueOr(import.meta.env.VITE_BRAND_PRIMARY_COLOR, "#6c369e"),
  accentColor: valueOr(import.meta.env.VITE_BRAND_ACCENT_COLOR, "#2fc95a"),
  allowTenantColor: valueOr(import.meta.env.VITE_ALLOW_TENANT_COLOR, "true") === "true",
  logoLight,
  logoLightSrcset: SIZED_LOGOS[logoLight]?.srcset,
  logoLightWidth: SIZED_LOGOS[logoLight]?.width,
  logoLightHeight: SIZED_LOGOS[logoLight]?.height,
  logoDark,
  logoDarkSrcset: SIZED_LOGOS[logoDark]?.srcset,
  logoDarkWidth: SIZED_LOGOS[logoDark]?.width,
  logoDarkHeight: SIZED_LOGOS[logoDark]?.height,
  privacyUrl: valueOr(
    import.meta.env.VITE_PRIVACY_URL,
    "https://www.coachio.digital/privacy-verklaring",
  ),
  supportUrl: valueOr(import.meta.env.VITE_SUPPORT_URL, "https://www.coachio.digital"),
  // Empty on purpose: a brand without its own terms page shows the sentence without a link.
  termsUrl: valueOr(import.meta.env.VITE_TERMS_URL, ""),
  // Empty means "no photo": the start screen keeps its gradient panel.
  heroImage: valueOr(import.meta.env.VITE_BRAND_HERO, ""),
  copy: parseBrandCopy(import.meta.env.VITE_BRAND_COPY),
  // Empty means "no brand font": the app keeps the Poppins heading stack.
  fontHeading: parseBrandFont(import.meta.env.VITE_BRAND_FONT_HEADING),
  marker: valueOr(
    import.meta.env.VITE_DISTRIBUTION_MARKER,
    "__COACHIO_DISTRIBUTION__=coachio",
  ),
});

export const DISTRIBUTION_HEADER = "X-Coachio-Distribution";
